const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ELASTIC_UUID = /^[A-Za-z0-9_-]{22}$/;

function isVisitorId(value) {
   return GUID.test(value) || ELASTIC_UUID.test(value);
}

export function normalizeTargets(targets) {
   if (!Array.isArray(targets)) return [];
   return [...new Set(targets.filter(value => typeof value === 'string').map(value => value.trim().toLowerCase().replace(/\.$/, ''))
      .filter(value => value && /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/.test(value)))];
}

export function hostMatches(hostname, targets) {
   const host = (hostname || '').toLowerCase().replace(/\.$/, '');
   return normalizeTargets(targets).some(target => host === target || host.endsWith(`.${target}`));
}

export function parseIncomingVisitor(url, clientShortName, targets) {
   let parsed;
   try { parsed = new URL(url); } catch { return null; }
   if (!['http:', 'https:'].includes(parsed.protocol) || !hostMatches(parsed.hostname, targets)) return null;
   const values = parsed.searchParams.getAll(`${clientShortName}_nbdxd`);
   return values.length === 1 && isVisitorId(values[0]) ? values[0] : null;
}

// Edit only our query field; URLSearchParams serialization changes other fields' bytes.
export function updateVisitorParam(href, key, value = null) {
   const hashIndex = href.indexOf('#');
   const hash = hashIndex < 0 ? '' : href.slice(hashIndex);
   const beforeHash = hashIndex < 0 ? href : href.slice(0, hashIndex);
   const queryIndex = beforeHash.indexOf('?');
   const base = queryIndex < 0 ? beforeHash : beforeHash.slice(0, queryIndex);
   const query = queryIndex < 0 ? [] : beforeHash.slice(queryIndex + 1).split('&');
   const kept = query.filter(field => {
      try { return decodeURIComponent(field.split('=')[0].replace(/\+/g, ' ')) !== key; }
      catch { return true; } // Preserve malformed host query fields verbatim.
   });
   if (value === null && kept.length === query.length) return href;
   if (value !== null) kept.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
   return base + (kept.length ? `?${kept.join('&')}` : '') + hash;
}

function isSignedUrl(url) {
   // Common CloudFront, AWS, Google Cloud and Azure signed URL fields.
   return [...url.searchParams.keys()].some(key => ['signature', 'sig', 'x-amz-signature', 'x-goog-signature'].includes(key.toLowerCase()));
}

function isExcluded(url, excludedPaths) {
   if (isSignedUrl(url)) return true;
   return Array.isArray(excludedPaths) && excludedPaths.some(path => {
      if (typeof path !== 'string' || !path.startsWith('/')) return false;
      const prefix = path.replace(/\/+$/, '');
      return url.pathname === prefix || url.pathname.startsWith(`${prefix}/`);
   });
}

export function decorateAnchor(anchor, clientShortName, visitorId, targets, currentHostname = window.location.hostname, excludedPaths = []) {
   if (!anchor || !anchor.href || !visitorId) return false;
   let url;
   try { url = new URL(anchor.href, window.location.href); } catch { return false; }
   const normalizedCurrentHostname = (currentHostname || '').toLowerCase().replace(/\.$/, '');
   const normalizedUrlHostname = (url.hostname || '').toLowerCase().replace(/\.$/, '');
   if (!['http:', 'https:'].includes(url.protocol) || normalizedUrlHostname === normalizedCurrentHostname || !hostMatches(url.hostname, targets)) return false;
   if (anchor.hasAttribute('download') || anchor.hasAttribute('data-hum-no-decorate') || url.username || url.password || isExcluded(url, excludedPaths)) return false;
   const key = `${clientShortName}_nbdxd`;
   const next = updateVisitorParam(url.href, key, visitorId);
   if (anchor.href !== next) anchor.href = next;
   return true;
}

export function createDecorator({clientShortName, visitorId, targets, excludedPaths = []}) {
   const normalized = normalizeTargets(targets);
   if (!normalized.length || !visitorId) return () => {};
   const getVisitorId = typeof visitorId === 'function' ? visitorId : () => visitorId;
   const owned = new Map();
   const removeOwned = anchor => {
      const ownedValue = owned.get(anchor);
      if (!ownedValue) return;
      try {
         const current = new URL(anchor.href, window.location.href);
         const key = `${clientShortName}_nbdxd`;
         if (!isSignedUrl(current) && current.searchParams.get(key) === ownedValue) {
            anchor.href = updateVisitorParam(current.href, key);
         }
      } catch { /* leave host-managed invalid URLs alone */ }
      owned.delete(anchor);
   };
   const decorate = anchor => {
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.isConnected === false) {
         removeOwned(anchor);
         return;
      }
      const currentVisitorId = getVisitorId();
      let previousValues = [];
      try { previousValues = new URL(anchor.href, window.location.href).searchParams.getAll(`${clientShortName}_nbdxd`); } catch { /* handled by decorateAnchor */ }
      if (decorateAnchor(anchor, clientShortName, currentVisitorId, normalized, window.location.hostname, excludedPaths)) {
         if (previousValues.length !== 1 || previousValues[0] !== currentVisitorId || owned.has(anchor)) owned.set(anchor, currentVisitorId);
      } else {
         removeOwned(anchor);
      }
   };
   const removeDetached = node => {
      if (node.nodeType !== 1 || node.isConnected) return;
      if (node.matches?.('a')) removeOwned(node);
      node.querySelectorAll?.('a').forEach(removeOwned);
   };
   // Observe removals only to release retained anchors. Never enforce href changes
   // against a host router, consent tool or another link decorator.
   const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => mutation.removedNodes?.forEach(removeDetached));
   });
   observer.observe(document.documentElement, {subtree: true, childList: true});
   const handler = event => decorate(event.target.closest?.('a[href]'));
   ['click', 'auxclick', 'contextmenu'].forEach(type => document.addEventListener(type, handler, true));
   return () => {
      observer.disconnect();
      ['click', 'auxclick', 'contextmenu'].forEach(type => document.removeEventListener(type, handler, true));
      owned.forEach((original, anchor) => {
         try {
            const current = new URL(anchor.href, window.location.href);
            if (current.searchParams.get(`${clientShortName}_nbdxd`) !== original) return;
            removeOwned(anchor);
         } catch { /* host page may have replaced the URL with an invalid value */ }
      });
   };
}
